import React from 'react';

import {
  Autocomplete,
  AutocompleteRenderInputParams,
  CircularProgress,
  FormControl,
  TextField,
  Typography,
  capitalize,
} from '@mui/material';
import classnames from 'classnames';

import {
  Location,
  SearchCityByNameResponseInner,
} from '@klokhuis/core/ApiClient/Klokhuis';
import { AppServicesContext } from '@klokhuis/core/AppServices';
import { useIntl } from '@klokhuis/core/Intl';
import debounce from 'debounce';

import styles from './LocationAutocomplete.module.css';
import { mockLocationsResponse } from './MockLocationResponse';

const areLocationsEqual = (first: Location, second: Location) =>
  first?.name === second?.name;

const getOptionLabel = (option: Location | null) => option?.name || '';

const filterOptions = (options: Location[]) => options; // we return the options as it is because filtering is done by server, not in client side.

export interface LocationAutocompleteProps {
  value: Location | null;
  borderless?: boolean;
  label?: React.ReactNode;
  placeholder?: string;
  InputProps?: Partial<AutocompleteRenderInputParams['InputProps']>;
  onChange: (value: Location | null) => void;
  onPopperToggle?: (isPopperOpen: boolean) => void;
}

const createLocationFromResponse = (
  item: SearchCityByNameResponseInner[],
): Location[] => {
  const Locations: Location[] = item.map(location => ({
    name: location.name,
    cityName: location.slug,
    id: location.citySlugId,
  }));
  return Locations;
};

export const LocationAutocomplete = ({
  value,
  borderless,
  label,
  placeholder,
  InputProps,
  onChange,
  onPopperToggle,
}: LocationAutocompleteProps) => {
  const { T } = useIntl();
  const abortControllerRef = React.useRef<AbortController | null>(null);
  const queryRef = React.useRef<string>(value?.name || '');
  const { apiClient } = AppServicesContext.useContext();
  const [loadingOptions, setLoadingOptions] = React.useState<boolean>(false);

  const [options, setOptions] = React.useState<Location[]>(
    value == null ? [] : [value],
  );

  // Keep queryRef and options in sync when value changes externally (e.g. navigation)
  React.useEffect(() => {
    queryRef.current = value?.name || '';
    if (value != null) {
      setOptions(prev =>
        prev.some(o => o.name === value.name) ? prev : [value, ...prev],
      );
    }
  }, [value]);

  const [popperPlaceholderText, setPopperPlaceholderText] =
    React.useState<string>(T('LBL_SELECT_PLACEHOLDER_CITY_EMPTY'));

  const textFieldRenderer = React.useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        label={
          label || (
            <Typography
              variant="body2"
              color="secondary.light"
              className={styles.placeHolder}
            >
              {capitalize(T('LBL_CITY_OR_POSTAL_CODE'))}
            </Typography>
          )
        }
        placeholder={placeholder}
        InputLabelProps={
          {
            'data-testid': 'location-autocomplete/label',
            ...params.InputLabelProps,
          } as any
        }
        size="medium"
        sx={{ width: '100%' }}
        InputProps={{
          ...params.InputProps,
          ...InputProps,
          autoComplete: 'off',
          endAdornment: (
            <React.Fragment>
              {loadingOptions && (
                <CircularProgress color="secondary" size={20} />
              )}
              {params.InputProps.endAdornment}
            </React.Fragment>
          ),
        }}
      />
    ),
    [T, loadingOptions, label, placeholder, InputProps],
  );

  const fetchOptionsDebounced = React.useMemo(() => {
    return debounce(() => {
      setLoadingOptions(true);
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      apiClient.gis
        .searchCityByName(
          {
            name: queryRef.current,
          },
          {
            signal: abortController.signal,
          },
        )
        .then(response => {
          setLoadingOptions(false);
          const locationOptions = response ?? [];
          setOptions(createLocationFromResponse(locationOptions));
          if (locationOptions.length === 0) {
            setPopperPlaceholderText(T('LBL_SELECT_CITY_NO_MATCH'));
          }
        })
        .catch(() => {
          const response = {} as any;
          setLoadingOptions(false);
          const locationOptions = response.locations ?? [];
          setOptions(locationOptions);
          if (locationOptions.length === 0) {
            setPopperPlaceholderText(T('LBL_SELECT_CITY_NO_MATCH'));
          }
          // continue regardless of error
        });
    }, 500);
  }, [T, apiClient]);

  const onQueryChange = React.useCallback(
    (_event: React.SyntheticEvent, query: string, reason: string) => {
      queryRef.current = query;
      abortControllerRef.current?.abort();
      if (query.length === 0) {
        setOptions([]);
        setPopperPlaceholderText(T('LBL_SELECT_PLACEHOLDER_CITY_EMPTY'));
      }

      if (reason !== 'input') {
        return;
      }

      if (query.length >= 1) {
        fetchOptionsDebounced();
      }
    },
    [T, fetchOptionsDebounced],
  );

  const onBlur = React.useCallback(() => {
    abortControllerRef.current?.abort();
    setLoadingOptions(false);
    if (
      value != null &&
      value.name &&
      queryRef.current.toLowerCase() !== value.name.toLocaleLowerCase()
    ) {
      onChange('' as unknown as Location);
    }
  }, [onChange, value]);

  const onLocationChangeInternal = React.useCallback(
    (_event: React.SyntheticEvent, location: Location | null) => {
      // When a value selected, we abort any existing API requests using the abort controller
      abortControllerRef.current?.abort();
      setLoadingOptions(false);
      onChange(location);
    },
    [onChange],
  );

  const onPopperOpen = React.useCallback(() => {
    onPopperToggle?.(true);
  }, [onPopperToggle]);

  const onPopperClose = React.useCallback(() => {
    onPopperToggle?.(false);
  }, [onPopperToggle]);

  return (
    <FormControl fullWidth className={styles.root}>
      <Autocomplete
        fullWidth
        className={classnames(
          borderless ? styles.noFloatingLabel : '',
          borderless ? styles.borderless : '',
        )}
        role="searchbox"
        value={value || null}
        loading={loadingOptions}
        options={options}
        renderInput={textFieldRenderer}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={areLocationsEqual}
        noOptionsText={
          <Typography variant="body3" color="secondary.light" role="tooltip">
            {popperPlaceholderText}
          </Typography>
        }
        clearOnBlur={false}
        filterOptions={filterOptions}
        data-testid="location-autocomplete"
        onChange={onLocationChangeInternal}
        placeholder={T('lbl.location')}
        onInputChange={onQueryChange}
        onOpen={onPopperOpen}
        onClose={onPopperClose}
        onBlur={onBlur}
      />
    </FormControl>
  );
};
